//Built-in libraries

import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
//Assets and Miscellaneous
import { HookCheckbox } from '@/components/UI/HookForm/Form/Checkbox';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { useFileUpload } from '@/hooks/useFileUpload';
import { compareTwoArrays, isObjectEmpty } from '@/utils';
import type { FacadeImprovementUploadPayload } from '../../types';
import { initialValues } from '../constants';
import { convertFulfilledFileUploadToAttachment } from './helpers';

type AttachmentsSectionKey = keyof typeof initialValues.step5;

//Component Begin

export default function Attachments() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');

  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    formState: { errors, isValid },
    setFocus,
    setValue,
    setError,
    watch,
  } = useFormContext();

  const data = watch('step5');

  const uploadFile = useMutation((payloads: FacadeImprovementUploadPayload[]) => Promise.allSettled(payloads.map((p) => upload(p))), {
    onSuccess: (results, payloads) => {
      const fileAttachments = convertFulfilledFileUploadToAttachment(results, getValues);

      updateData({
        step5: {
          ...getValues('step5'),
          ...fileAttachments,
        },
      });

      // If some file upload is failed, raise error and remove file from state
      const isUploadFailed = results.some((r) => r.status === 'rejected');
      if (isUploadFailed) {
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            setValue(`step5.${payloads[index].field.key}.selectedFiles`, []);
            setError(
              `step5.${payloads[index].field.key}.selectedFiles`,
              {
                message: 'Failed to upload',
              },
              {
                shouldFocus: index === 0,
              }
            );
            updateData({
              step5: {
                ...formContextData.step5,
                [payloads[index].field.key]: initialValues.step5[payloads[index].field.key as AttachmentsSectionKey],
              },
            });
          }
        });
        return;
      }

      setCurrentStep((prev) => prev + 1);
    },

    onError: (error: any) => {
      console.log(error);
      setUploadError('Failed to upload');
    },
  });

  const fileRemoveHandler = (index: number, name: string) => {
    const updatedFormContextAttachments = [...formContextData.step5[name].selectedFiles];
    updatedFormContextAttachments.splice(index, 1);

    updateData({
      step5: {
        ...formContextData.step5,
        [name]: {
          ...formContextData.step5[name],
          selectedFiles: updatedFormContextAttachments,
        },
      },
    });
  };

  useEffect(() => {
    if (errors?.step5 && !isObjectEmpty(errors)) {
      const firstErrorKey = Object.keys(errors?.step5)[0];
      const firstErrorChildKey = Object.keys(get(errors, `step5.${firstErrorKey}`))[0];
      setFocus(`step5.${firstErrorKey}.${firstErrorChildKey}`);
    }
  }, [errors, setFocus]);

  const buildFileUploadPayload = (): FacadeImprovementUploadPayload[] => {
    const formData = getValues('step5');
    const payloads: FacadeImprovementUploadPayload[] = [];

    Object.keys(formData).forEach((f: string) => {
      const { selectedFiles } = formData[f];
      if (selectedFiles.length > 0) {
        const formDataSelectedFiles = selectedFiles.map((f: File) => f.name);
        const formContextDataUploadedFiles: string[] = formContextData?.step5[f].selectedFiles
          .map((sf: File) => sf.name)
          .filter((fn: string | undefined) => fn);

        const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

        //Uploading only new file names
        if (fileUploadCompare.array1Diff.length > 0) {
          const signAssetPayload = selectedFiles
            .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
            .map((file: File) => ({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              formId: String(params.applicationFormId),
              field: { key: f, description: f },
            }));
          payloads.push({
            signAssetPayload,
            files: selectedFiles,
            field: { key: f, description: f },
          });
        }
      }
    });

    return payloads;
  };

  const onSubmit = async () => {
    trigger();
    if (!isValid) {
      return;
    }
    const formData = getValues('step5');

    const fileUploadPayload = buildFileUploadPayload();
    if (fileUploadPayload.length > 0) {
      uploadFile.mutate(fileUploadPayload);
    } else {
      updateData({
        step5: { ...formData },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <div>
        <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Attachments</h3>
        <p className='mt-1 text-sm text-gray-500'>
          Please provide the following documents. If you do not have the requested document, please explain why:
        </p>
      </div>
      <div className='grid grid-cols-6 gap-6'>
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f1.selectedFiles'
            label='Drawing and Specifications'
            id='step5.f1.selectedFiles'
            onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'f1')}
            multiple
          />
        </div>
        {data?.f1?.selectedFiles?.length === 0 && (
          <>
            <HookCheckbox name='step5.f1.haveFiles' id='have-files-f1' className='col-span-6 self-end'>
              <label htmlFor='have-files-f1'>I do not have drawing and specifications</label>
            </HookCheckbox>
            {data?.f1?.haveFiles && (
              <div className='col-span-6'>
                <HookTextarea
                  name='step5.f1.haveFilesDescription'
                  label='Please describe why you do not have drawings and specifications'
                  placeholder='description'
                  rows={5}
                />
              </div>
            )}
          </>
        )}
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f2.selectedFiles'
            label='Building Photographs'
            id='step5.f2.selectedFiles'
            onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'f2')}
            multiple
          />
        </div>
        {data?.f2?.selectedFiles?.length === 0 && (
          <>
            <HookCheckbox name='step5.f2.haveFiles' id='have-files-f2' className='col-span-6 self-end'>
              <label htmlFor='have-files-f2'>I do not have photographs of building</label>
            </HookCheckbox>
            {data?.f2?.haveFiles && (
              <div className='col-span-6'>
                <HookTextarea
                  name='step5.f2.haveFilesDescription'
                  label='Please describe why you do not have building photographs'
                  placeholder='description'
                  rows={5}
                />
              </div>
            )}
          </>
        )}
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f3.selectedFiles'
            label='Letters of commitment (others involved in financing)'
            id='step5.f3.selectedFiles'
            onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'f3')}
            multiple
          />
        </div>
        {data?.f3?.selectedFiles?.length === 0 && (
          <>
            <HookCheckbox name='step5.f3.haveFiles' id='have-files-f3' className='col-span-6 self-end'>
              <label htmlFor='have-files-f3'>I do not have letter of commitment</label>
            </HookCheckbox>
            {data?.f3?.haveFiles && (
              <div className='col-span-6'>
                <HookTextarea
                  name='step5.f3.haveFilesDescription'
                  label='Please describe why you do not have letters of commitment'
                  placeholder='description'
                  rows={5}
                />
              </div>
            )}
          </>
        )}
        <div className='col-span-6'>
          <HookFileInput
            name='step5.f4.selectedFiles'
            label='Personal financial statements for each building owner.'
            id='step5.f4.selectedFiles'
            onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'f4')}
            multiple
          />
        </div>
        {data?.f4?.selectedFiles?.length === 0 && (
          <>
            <HookCheckbox name='step5.f4.haveFiles' id='have-files-f4' className='col-span-6 self-end'>
              <label htmlFor='have-files-f4'>I do not have financial statements</label>
            </HookCheckbox>
            {data?.f4?.haveFiles && (
              <div className='col-span-6'>
                <HookTextarea
                  name='step5.f4.haveFilesDescription'
                  label='Please describe why you do not have personal financial statements'
                  placeholder='description'
                  rows={5}
                />
              </div>
            )}
          </>
        )}
        <div className='col-span-6'>
          <HookFileInput
            name='step5.w9Form.selectedFiles'
            label='W-9 form'
            id='step5.w9Form.selectedFiles'
            onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'w9Form')}
          />
          <a href='#' className='mt-2 text-neutralColor-normal text-xs flex items-center gap-1 hover:underline'>
            <span>Download Sample: W-9 Form</span>
            <ArrowDownTrayIcon className='w-4 h-4' />
          </a>
        </div>
        <div className='col-span-6'>
          <HookFileInput
            name='step5.transparencyForm.selectedFiles'
            label='Transparency form'
            id='step5.transparencyForm.selectedFiles'
            onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'transparencyForm')}
          />
          <a href='#' className='mt-2 text-neutralColor-normal text-xs flex items-center gap-1 hover:underline'>
            <span>Download Sample: Transparency Form</span>
            <ArrowDownTrayIcon className='w-4 h-4' />
          </a>
        </div>
        <div className='col-span-6'>
          <HookFileInput
            name='step5.businessLicense.selectedFiles'
            label='Business License'
            id='step5.businessLicense.selectedFiles'
            onFileRemove={(fileIndex: number) => fileRemoveHandler(fileIndex, 'businessLicense')}
          />
        </div>
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
